import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SortableTableHeader } from '../components/ui/sortable-table-header';
import { Plus, MagnifyingGlass, Trash, Key, Users, ShieldCheck, Eye, SpinnerGap, Calendar, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import InfoTooltip from '../components/InfoTooltip';
import EmptyState from '../components/EmptyState';

const PAGE_SIZE = 20;

export default function EntitlementsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', type: 'security' });
  const [selectedEntitlement, setSelectedEntitlement] = useState(null);
  const [entitlementData, setEntitlementData] = useState(null);
  const [loadingIdentities, setLoadingIdentities] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [typeFilter, setTypeFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const skip = (currentPage - 1) * PAGE_SIZE;
      const res = await API.get('/entitlements', { 
        params: { 
          search: search || undefined, 
          limit: PAGE_SIZE, 
          skip: skip,
          sort_by: sortBy,
          sort_order: sortOrder,
          type: typeFilter || undefined,
          source: sourceFilter || undefined
        } 
      });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (err) { toast.error('Failed to load entitlements'); }
  }, [search, currentPage, sortBy, sortOrder, typeFilter, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, sortOrder, typeFilter, sourceFilter]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSort = (column, order) => {
    setSortBy(column);
    setSortOrder(order);
  };

  const handleCreate = async () => {
    try {
      await API.post('/entitlements', form);
      toast.success('Entitlement created');
      setShowDialog(false);
      setForm({ name: '', description: '', type: 'security' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entitlement?')) return;
    try { await API.delete(`/entitlements/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const handleView = async (entitlement) => {
    setSelectedEntitlement(entitlement);
    setShowViewDialog(true);
    setLoadingIdentities(true);
    setEntitlementData(null);
    
    try {
      const res = await API.get(`/entitlements/${entitlement.id}/identities`);
      setEntitlementData(res.data);
    } catch (err) {
      toast.error('Failed to load entitlement details');
    } finally {
      setLoadingIdentities(false);
    }
  };

  const typeColor = (t) => {
    if (t === 'security') return 'bg-blue-100 text-blue-700';
    if (t === 'microsoft365') return 'bg-purple-100 text-purple-700';
    return 'bg-slate-100 text-navy-600';
  };

  return (
    <div data-testid="entitlements-page" className="p-4 sm:p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Entitlements <InfoTooltip concept="entitlements_page" /></h1>
          <p className="text-sm text-navy-500 mt-1">{total} entitlements (Azure AD groups)</p>
        </div>
        <Button onClick={() => { setForm({ name: '', description: '', type: 'security' }); setShowDialog(true); }} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm shadow-sm" data-testid="create-entitlement-button">
          <Plus size={16} className="mr-1.5" /> New Entitlement
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
          <Input placeholder="Search entitlements..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 rounded-sm border-slate-200" data-testid="entitlement-search-input" />
        </div>
        <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32 h-9 rounded-sm text-xs" data-testid="entitlement-type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="security">Security</SelectItem>
            <SelectItem value="distribution">Distribution</SelectItem>
            <SelectItem value="microsoft365">Microsoft 365</SelectItem>
            <SelectItem value="mail_enabled">Mail Enabled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceFilter || 'all'} onValueChange={(v) => setSourceFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32 h-9 rounded-sm text-xs" data-testid="entitlement-source-filter">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="azure_ad">Azure AD</SelectItem>
            <SelectItem value="manual">Manual</SelectItem>
          </SelectContent>
        </Select>
        {(typeFilter || sourceFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setTypeFilter(''); setSourceFilter(''); }} className="text-xs text-navy-500 h-9">Clear filters</Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="h-1.5 bg-navy-600" />
        <Table wrapperClassName="max-h-[calc(100vh-340px)]">
          <TableHeader>
            <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
              <SortableTableHeader column="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Name</SortableTableHeader>
              <SortableTableHeader column="description" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Description</SortableTableHeader>
              <SortableTableHeader column="type" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Type</SortableTableHeader>
              <SortableTableHeader column="source" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Source</SortableTableHeader>
              <SortableTableHeader column="member_count" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Members</SortableTableHeader>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={6}><EmptyState type="entitlements" onAction={() => setShowDialog(true)} /></TableCell></TableRow>
            ) : items.map((item) => (
              <TableRow key={item.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => handleView(item)} data-testid={`entitlement-row-${item.id}`}>
                <TableCell className="font-medium text-sm text-navy-900">
                  <div className="flex items-center gap-2"><Key size={14} className="text-orange-500" />{item.name}</div>
                </TableCell>
                <TableCell className="text-sm text-navy-600 max-w-xs truncate">{item.description || '-'}</TableCell>
                <TableCell><span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${typeColor(item.type)}`}>{item.type}</span></TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{item.source || 'manual'}</Badge></TableCell>
                <TableCell className="text-sm text-navy-600">{item.member_count || 0}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleView(item)} className="text-navy-600 hover:text-navy-900" data-testid={`view-entitlement-${item.id}`}>
                      <Eye size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-entitlement-${item.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {total > 0 && (
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm shadow-sm px-4 py-3">
          <div className="text-sm text-navy-500">
            Showing <span className="font-medium text-navy-700">{((currentPage - 1) * PAGE_SIZE) + 1}</span> to{' '}
            <span className="font-medium text-navy-700">{Math.min(currentPage * PAGE_SIZE, total)}</span> of{' '}
            <span className="font-medium text-navy-700">{total}</span> entitlements
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-sm h-8 px-3"
            >
              <CaretLeft size={14} className="mr-1" /> Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`rounded-sm h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="rounded-sm h-8 px-3"
            >
              Next <CaretRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Create Entitlement</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="entitlement-name-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="security">Security Group</SelectItem>
                  <SelectItem value="microsoft365">Microsoft 365 Group</SelectItem>
                  <SelectItem value="distribution">Distribution Group</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleCreate} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-entitlement-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Entitlement Dialog */}
      <Dialog open={showViewDialog} onOpenChange={(open) => { setShowViewDialog(open); if (!open) setEntitlementData(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-2">
                <Key size={20} className="text-orange-500" />
                {selectedEntitlement?.name}
              </div>
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="bg-slate-100 rounded-sm p-0.5 shrink-0">
              <TabsTrigger value="details" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2">
                <Key size={14} className="mr-1.5" /> Details
              </TabsTrigger>
              <TabsTrigger value="identities" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2">
                <Users size={14} className="mr-1.5" /> Identities
                {entitlementData?.summary?.total_identities > 0 && (
                  <Badge className="ml-1.5 bg-blue-100 text-blue-700 text-[10px]">{entitlementData.summary.total_identities}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="roles" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2">
                <ShieldCheck size={14} className="mr-1.5" /> Roles
                {entitlementData?.roles?.length > 0 && (
                  <Badge className="ml-1.5 bg-purple-100 text-purple-700 text-[10px]">{entitlementData.roles.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="flex-1 overflow-auto mt-4">
              {selectedEntitlement && (
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-sm p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] font-bold uppercase text-navy-500">Name</p>
                        <p className="text-sm text-navy-900 font-medium">{selectedEntitlement.name}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-navy-500">Type</p>
                        <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${typeColor(selectedEntitlement.type)}`}>
                          {selectedEntitlement.type}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase text-navy-500">Description</p>
                      <p className="text-sm text-navy-600">{selectedEntitlement.description || 'No description'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] font-bold uppercase text-navy-500">Source</p>
                        <Badge variant="outline" className="text-[10px]">{selectedEntitlement.source || 'manual'}</Badge>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-navy-500">Azure ID</p>
                        <p className="text-xs text-navy-500 font-mono">{selectedEntitlement.azure_id || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Summary Stats */}
                  {entitlementData && (
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 text-center">
                        <p className="text-2xl font-bold text-blue-700">{entitlementData.summary.total_identities}</p>
                        <p className="text-xs text-blue-600">Total Identities</p>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-sm p-4 text-center">
                        <p className="text-2xl font-bold text-purple-700">{entitlementData.summary.identities_via_roles || 0}</p>
                        <p className="text-xs text-purple-600">Via Roles</p>
                      </div>
                      <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-4 text-center">
                        <p className="text-2xl font-bold text-cyan-700">{entitlementData.summary.identities_via_direct_azure || 0}</p>
                        <p className="text-xs text-cyan-600">Direct Azure AD</p>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-center">
                        <p className="text-2xl font-bold text-emerald-700">{entitlementData.summary.total_roles}</p>
                        <p className="text-xs text-emerald-600">Roles</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="identities" className="flex-1 overflow-auto mt-4">
              {loadingIdentities ? (
                <div className="flex items-center justify-center py-12">
                  <SpinnerGap className="h-6 w-6 animate-spin text-orange-500" />
                  <span className="ml-2 text-sm text-navy-500">Loading identities...</span>
                </div>
              ) : entitlementData?.identities?.length > 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Identity</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Department</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Access Source</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Assignment Type</TableHead>
                        <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Expires</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entitlementData.identities.map((identity, idx) => (
                        <TableRow key={`${identity.id}-${idx}`} className="hover:bg-slate-50">
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm text-navy-900">{identity.display_name}</p>
                              <p className="text-xs text-navy-500">{identity.email || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-navy-600">{identity.department || '-'}</TableCell>
                          <TableCell>
                            {identity.access_type === 'direct_azure_ad' ? (
                              <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">
                                <Key size={10} className="mr-1" />Direct Azure AD
                              </Badge>
                            ) : (
                              <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                                <ShieldCheck size={10} className="mr-1" />{identity.role_name}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] ${
                              identity.assignment_type === 'automatic' ? 'bg-blue-100 text-blue-700' : 
                              identity.assignment_type === 'azure_ad_direct' ? 'bg-cyan-100 text-cyan-700' : 
                              'bg-slate-100 text-navy-600'
                            }`}>
                              {identity.assignment_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-navy-500">
                            {identity.expires_at ? (
                              <div className="flex items-center gap-1 text-amber-600">
                                <Calendar size={12} />
                                {new Date(identity.expires_at).toLocaleDateString()}
                              </div>
                            ) : (
                              <span className="text-navy-400">No expiry</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12 text-sm text-navy-400">
                  <Users size={32} className="mx-auto mb-2 text-navy-300" />
                  No identities have access to this entitlement
                </div>
              )}
            </TabsContent>

            <TabsContent value="roles" className="flex-1 overflow-auto mt-4">
              {loadingIdentities ? (
                <div className="flex items-center justify-center py-12">
                  <SpinnerGap className="h-6 w-6 animate-spin text-orange-500" />
                  <span className="ml-2 text-sm text-navy-500">Loading roles...</span>
                </div>
              ) : entitlementData?.roles?.length > 0 ? (
                <div className="space-y-3">
                  {entitlementData.roles.map((role) => (
                    <div key={role.id} className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-purple-100 flex items-center justify-center">
                          <ShieldCheck size={20} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-navy-900">{role.name}</p>
                          <p className="text-xs text-navy-500">
                            Role ID: {role.id}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-sm text-navy-400">
                  <ShieldCheck size={32} className="mx-auto mb-2 text-navy-300" />
                  This entitlement is not assigned to any roles
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="shrink-0 mt-4 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={() => setShowViewDialog(false)} className="rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
